import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/hooks/useLanguage';
import { BlogArticle, categoryLabels, blogTranslations } from '@/lib/blogData';
import { BlogCoverCrisis, BlogCoverTemplates, BlogCoverGoogleMaps, BlogCoverSuccessStories, BlogCoverGoogleReviews2026 } from './covers';

interface BlogCardProps {
  article: BlogArticle;
  index: number;
}

// Map article slugs to their custom cover components
const coverComponents: Record<string, React.ComponentType> = {
  'chantage-avis-negatif-guide': BlogCoverCrisis,
  '7-modeles-reponses-avis-google': BlogCoverTemplates,
  'devant-concurrents-google-maps-30-jours': BlogCoverGoogleMaps,
  'success-stories-nos-clients-temoignent': BlogCoverSuccessStories,
  'pourquoi-avis-google-essentiels-2026': BlogCoverGoogleReviews2026,
};

export function BlogCard({ article, index }: BlogCardProps) {
  const { language } = useLanguage();
  const t = blogTranslations[language];
  
  // Check if article has a custom cover component
  const CoverComponent = coverComponents[article.slug];

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="group"
    >
      <Link to={`/blog/${article.slug}`} className="block">
        <div className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-lg hover:border-primary/30 transition-all duration-300">
          {/* Cover Image or Custom Component */}
          <div className="aspect-video bg-muted relative overflow-hidden">
            {CoverComponent ? (
              <CoverComponent />
            ) : (
              <img
                src={article.coverImage}
                alt={article.title[language]}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            )}
            <div className="absolute top-4 left-4 z-10">
              <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">
                {categoryLabels[article.category][language]}
              </Badge>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <Clock className="h-4 w-4" />
              <span>{article.readingTime} {t.readingTime}</span>
            </div>
            
            <h2 className="text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
              {article.title[language]}
            </h2>
            
            <p className="text-muted-foreground line-clamp-2 mb-4">
              {article.excerpt[language]}
            </p>
            
            <span className="inline-flex items-center gap-2 text-primary font-medium text-sm group-hover:gap-3 transition-all">
              {t.readMore}
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
